import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import * as UserAccessActions from './user-access.actions';
import {setUserAccessSettingsDefaults} from './user-access.actions';
import * as UserActions from '../user-store/user.actions';
import {IGroupAccess, IUserAccess, IUserAccessToken, IUserAccessTokenURL} from '../models';
import {ApplicationFeatureKeys, ApplicationFeaturesEnum} from '../../../shared/shared-utils/features/features';
import {looker_studio_base_url} from '../../../shared/shared-services/database-paths';
import {environment} from '../../../../environments/environment';
import {getDeepCopyOfObject} from '../../../shared/shared-utils/object/object.utils';
import {getStoreAccessFlags} from '../utils/access.utils';
import {encodeStrict} from '../../../shared/shared-utils/firestore/firestore.utils';
import {IStore} from '../../../shared/shared-models/store/store';
import {_SETTINGS_STATE_DEFAULTS, ISettingsState} from '../models/user-access-features/user-access-features';
import {
  _DEFAULT_USER_ACCESS_OPEN_ORDER_SETTINGS,
  _DEFAULT_USER_ACCESS_ORDER_SETTINGS,
  IUserAccessFeatureAutoOrderingSettings,
} from '../models/user-access-features/access';
import {
  _DEFAULT_USER_ACCESS_SHELF_TALKER_SETTINGS,
  _DEFAULT_USER_ACCESS_STOCK_RULES,
  _DEFAULT_USER_ACCESS_STOCK_TAKE_SETTINGS,
  IUserAccessFeatureShelfTalkerSettings,
  IUserAccessFeatureStockRules,
  IUserAccessFeatureStockTakeSettings,
} from '../models/user-access-features/access';
import {IUserAccessOperationProgress} from '../models/user-access-operation-progress.interface';

export interface IUserAccessState {
  groupAccess: IGroupAccess;
  groupAccessLoaded: boolean;
  isGroupAccessLoading: boolean;
  isLoading: boolean;
  isUserAccessLoading: boolean;
  isUserAccessTokenLoading: boolean;
  lastFailedColleagueAccessUpdateUserId: string | null;
  lastFailedStaffAccessCreateUserId: string | null;
  operationProgress: IUserAccessOperationProgress | null;
  userAccess: IUserAccess | undefined;
  userAccessFeatureSettings: ISettingsState;
  userAccessPerFeature: Record<string, IStore[]>;
  userAccessToken: IUserAccessToken | IUserAccessTokenURL<unknown> | undefined;
  userAccessTokenFeature: ApplicationFeatureKeys | undefined;
}

export const initialUserAccessState: IUserAccessState = {
  groupAccess: {},
  groupAccessLoaded: false,
  isGroupAccessLoading: false,
  isLoading: false,
  isUserAccessLoading: false,
  isUserAccessTokenLoading: false,
  lastFailedColleagueAccessUpdateUserId: null,
  lastFailedStaffAccessCreateUserId: null,
  operationProgress: null,
  userAccess: undefined,
  userAccessFeatureSettings: {..._SETTINGS_STATE_DEFAULTS},
  userAccessPerFeature: {},
  userAccessToken: undefined,
  userAccessTokenFeature: undefined,
};

const createUserAccessReducer: ActionReducer<IUserAccessState> = createReducer(
  getDeepCopyOfObject(initialUserAccessState),
  //#region LOG OUT USER
  on(UserActions.logOutUser, (state: IUserAccessState) => ({
    ...getDeepCopyOfObject(initialUserAccessState),
  })),
  //#endregion
  //#region GLOBAL
  on(UserAccessActions.setPropertyOnUserAccessState, (state: IUserAccessState, {property, value}) => {
    return {
      ...state,
      [property]: value,
    };
  }),
  on(UserAccessActions.clearUserAccessState, (state: IUserAccessState) => ({
    ...getDeepCopyOfObject(initialUserAccessState),
  })),
  //#endregion
  //#region GET USER ACCESS
  on(UserAccessActions.getUserAccess, (state) => ({
    ...state,
    isUserAccessLoading: true,
  })),
  on(UserAccessActions.getUserAccessSuccess, (state, {userAccess}) => {
    return {
      ...state,
      isUserAccessLoading: false,
      userAccess: getStoreAccessFlags(userAccess),
    };
  }),
  //#endregion
  //#region SET DEFAULT SETTINGS
  on(setUserAccessSettingsDefaults.setDefaultSettings, (state) => {
    const storesList: string[] = state.userAccess.storeList ?? <string[]>[];
    const autoOrderingCrud: { [storeId: string]: IUserAccessFeatureAutoOrderingSettings } = {};
    const shelfTalkerGeneralCrud: { [storeId: string]: IUserAccessFeatureShelfTalkerSettings } = {};
    const stockItemRulesCrud: { [storeId: string]: IUserAccessFeatureStockRules } = {};
    const stockTakeCrud: { [storeId: string]: IUserAccessFeatureStockTakeSettings } = {};
    storesList.forEach((storeId: string) => {
      autoOrderingCrud[storeId] = {
        orderSettings: {..._DEFAULT_USER_ACCESS_ORDER_SETTINGS},
        openOrderSettings: {..._DEFAULT_USER_ACCESS_OPEN_ORDER_SETTINGS},
      };
      shelfTalkerGeneralCrud[storeId] = {overrides: {..._DEFAULT_USER_ACCESS_SHELF_TALKER_SETTINGS}};
      stockItemRulesCrud[storeId] = {..._DEFAULT_USER_ACCESS_STOCK_RULES};
      stockTakeCrud[storeId] = {overrides: {..._DEFAULT_USER_ACCESS_STOCK_TAKE_SETTINGS}};
    });
    return {
      ...state,
      userAccessFeatureSettings: {
        ...state.userAccessFeatureSettings,
        features: {
          ...state.userAccessFeatureSettings.features,
          ['auto-ordering']: {
            ...state.userAccessFeatureSettings.features['auto-ordering'],
            crudAccess: autoOrderingCrud,
          },
          ['shelf-talkers']: {
            ...state.userAccessFeatureSettings.features['shelf-talkers'],
            crudAccess: shelfTalkerGeneralCrud,
          },
          ['stock-rules']: {
            ...state.userAccessFeatureSettings.features['stock-rules'],
            crudAccess: stockItemRulesCrud,
          },
          ['stock-take']: {
            ...state.userAccessFeatureSettings.features['stock-take'],
            crudAccess: stockTakeCrud,
          },
        },
      },
    };
  }),
  //#endregion
  //#region STAFF ACCESS CREATE COLLEAGUE
  on(UserAccessActions.createStaffAccessColleague, (state) => ({
    ...state,
    isLoading: true,
    lastFailedStaffAccessCreateUserId: null,
    operationProgress: {
      step: 1,
      totalSteps: 2,
      message: 'Creating user account…',
      stepLabels: ['Create account', 'Apply store permissions'],
    },
  })),
  on(UserAccessActions.setUserAccessOperationProgress, (state, {progress}) => ({
    ...state,
    operationProgress: progress,
  })),
  on(UserAccessActions.createStaffAccessColleagueSuccess, (state, _) => ({
    ...state,
    isLoading: false,
    lastFailedStaffAccessCreateUserId: null,
    operationProgress: null,
  })),
  on(UserAccessActions.createStaffAccessColleagueFailure, (state, {userId}) => ({
    ...state,
    isLoading: false,
    lastFailedStaffAccessCreateUserId: userId,
    operationProgress: null,
  })),
  on(UserAccessActions.clearLastFailedStaffAccessCreateUserId, (state) => ({
    ...state,
    lastFailedStaffAccessCreateUserId: null,
  })),
  on(UserAccessActions.setPermissionsFailure, (state, {error, userId}) => ({
    ...state,
    isLoading: false,
    lastFailedStaffAccessCreateUserId: userId ?? error.userId ?? state.lastFailedStaffAccessCreateUserId,
    operationProgress: null,
  })),
  on(UserAccessActions.setColleaguesUserAccess, (state) => ({
    ...state,
    isLoading: true,
    lastFailedColleagueAccessUpdateUserId: null,
    operationProgress: {
      step: 1,
      totalSteps: 1,
      message: 'Saving access permissions…',
      stepLabels: ['Save permissions'],
    },
  })),
  on(UserAccessActions.setColleaguesUserAccessSuccess, (state) => ({
    ...state,
    isLoading: false,
    lastFailedColleagueAccessUpdateUserId: null,
    operationProgress: null,
  })),
  on(UserAccessActions.setColleaguesUserAccessFailure, (state, {userId}) => ({
    ...state,
    isLoading: false,
    lastFailedColleagueAccessUpdateUserId: userId,
    operationProgress: null,
  })),
  on(UserAccessActions.getGroupAccessSettings, (state) => ({
    ...state,
    isGroupAccessLoading: state.groupAccessLoaded ? state.isGroupAccessLoading : true,
  })),
  on(UserAccessActions.getGroupAccessSettingsSuccess, (state, {groupAccess}) => ({
    ...state,
    groupAccess,
    groupAccessLoaded: true,
    isGroupAccessLoading: false,
  })),
  on(UserAccessActions.getGroupAccessSettingsFailure, (state, {error}) => ({
    ...state,
    groupAccessLoaded: true,
    isGroupAccessLoading: false,
  })),
  on(UserAccessActions.getUserAccessToken, (state, {appFeature}) => ({
    ...state,
    isUserAccessTokenLoading: true,
    userAccessToken: undefined,
    userAccessTokenFeature: appFeature,
  })),
  on(UserAccessActions.getUserAccessTokenSuccess, (state, {appFeature, userAccessToken}) => {
    let userAccessTokenReceived = userAccessToken;
    if (appFeature === ApplicationFeaturesEnum.DASHBOARD_REPORT && userAccessTokenReceived.token) {
      const baseURL = looker_studio_base_url(String(environment.lookerStudio.apiKey));
      const params: Record<string, string> = {
        userToken: userAccessToken.token,
        userTokenStock: userAccessToken.token,
      };
      const encodedParams = encodeStrict(JSON.stringify(params));
      const fullUrl = `${baseURL}?params=${encodedParams}`;
      userAccessTokenReceived = {
        ...userAccessToken,
        baseUrl: baseURL,
        params,
        encodedParams: encodeStrict(JSON.stringify(params)),
        fullUrl,
      } as unknown as IUserAccessTokenURL<{ userToken: string; }>;
    }
    return {
      ...state,
      isUserAccessTokenLoading: false,
      userAccessToken: userAccessTokenReceived,
      userAccessTokenFeature: appFeature,
    };
  }),
  on(UserAccessActions.getUserAccessTokenFailure, (state, {appFeature, error}) => ({
    ...state,
    isUserAccessTokenLoading: false,
    userAccessTokenFeature: appFeature,
  })),
  on(UserAccessActions.getUserAccessPerFeature, (state, {accessMap}) => ({
    ...state,
    userAccessPerFeature: accessMap,
  })),
  on(UserAccessActions.setUserAccessToken, (state, {appFeature, userAccessToken}) => ({
    ...state,
    isUserAccessTokenLoading: false,
    userAccessToken: userAccessToken,
    userAccessTokenFeature: appFeature,
  })),
  //#endregion

);


export const userAccessReducer = (state: IUserAccessState, action: Action): IUserAccessState => createUserAccessReducer(state, action);
